import React, { useState, useEffect } from 'react';
import './ChatBot.css';
import llmService from './services/llmService';

const ChatBot = ({ apiConfig, systemPrompt }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { text: "Hello! How can I help you today?", sender: "bot" }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');

  // Set system message on component mount
  useEffect(() => {
    if (systemPrompt) {
      llmService.setSystemMessage(systemPrompt);
    }
  }, [systemPrompt]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inputText.trim()) return;
    
    // Add user message to chat
    const userMessage = { text: inputText, sender: "user" };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputText('');
    
    // Show bot is typing
    setIsTyping(true);
    
    try {
      if (apiConfig?.defaultParams?.stream) {
        // Use streaming mode
        setIsStreaming(true);
        setStreamingMessage('');
        
        await llmService.streamMessage(
          userMessage.text,
          (chunk) => {
            setStreamingMessage(prev => prev + chunk);
          }
        );
        
        // Add the complete streamed message to chat history
        setMessages(prevMessages => [
          ...prevMessages, 
          { text: streamingMessage, sender: "bot" }
        ]);
        
        setIsStreaming(false);
      } else {
        // Use non-streaming mode
        const response = await llmService.sendMessage(userMessage.text);
        setMessages(prevMessages => [
          ...prevMessages, 
          { text: response, sender: "bot" }
        ]);
      }
    } catch (error) {
      console.error("Error getting response from LLM:", error);
      setMessages(prevMessages => [
        ...prevMessages, 
        { 
          text: `Sorry, I'm having trouble connecting. Error: ${error.message}`, 
          sender: "bot" 
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleResetConversation = () => {
    llmService.resetConversation();
    if (systemPrompt) {
      llmService.setSystemMessage(systemPrompt);
    }
    setMessages([{ text: "Hello! How can I help you today?", sender: "bot" }]);
  };

  useEffect(() => {
    // Scroll to bottom of messages when new messages arrive
    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) {
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }
  }, [messages, streamingMessage]);

  return (
    <div className="chatbot-container">
      {/* Chat bubble button */}
      <button 
        className={`chat-bubble ${isOpen ? 'open' : ''}`} 
        onClick={toggleChat}
        aria-label="Toggle chat"
      >
        {isOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path fill="currentColor" d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
          </svg>
        )}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <h3>Island Assets Assistant</h3>
            <button 
              className="reset-button" 
              onClick={handleResetConversation}
              title="Reset conversation"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
            </button>
          </div>
          
          <div className="chat-messages" id="chat-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.sender}`}>
                {msg.text}
              </div>
            ))}
            
            {/* Show streaming message while it's coming in */}
            {isStreaming && (
              <div className="message bot">
                {streamingMessage || (
                  <div className="typing">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                )}
              </div>
            )}
            
            {/* Show typing indicator when not streaming */}
            {isTyping && !isStreaming && (
              <div className="message bot typing">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            )}
          </div>
          
          <form className="chat-input-form" onSubmit={handleSubmit}>
            <input
              type="text"
              value={inputText}
              onChange={handleInputChange}
              placeholder="Type your message here..."
              className="chat-input"
              disabled={isTyping}
            />
            <button 
              type="submit" 
              className="send-button"
              disabled={isTyping || !inputText.trim()}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatBot;